import { Recurrence, SelfServiceSchedulePaneProps } from '@tableau/self-service-schedules';
import _ from 'underscore';

import { SchedulingErrorCode, WebClientErrorCode } from '~commonInfra/utils/ErrorCodes';
import { Set } from '~commonInfra/utils/Set';
import { SessionServerSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { ActionHelpers, standardActionErrorMsg, standardActionSuccessMsg } from '~src/commonInfra/contentActions/ActionHelpers';
import { IErrorDetail } from '~src/commonInfra/contentActions/ActionMessages';
import { IErrorItem } from '~src/commonInfra/contentActions/MultipleItemResult';
import { IDataSourceBase, IDataSourceDetailInfo } from '~src/datasource/models/IDataSourceInfo';
import { IRemoteRefreshAgentOption, IRemoteRefreshPropertiesEditModel } from '~src/datasource/services/ChangeDatasourceRemoteRefreshPropertiesHelper';
import { CreateExtractRefreshAction } from '~src/extractRefresh/actions/extractRefresh/CreateExtractRefreshAction';
import { RefreshPropertyHelper } from '~src/extractRefresh/helpers/RefreshPropertyHelper';
import { OpenRefreshSelfServiceScheduleDialogParameters } from '~src/extractTask/components/extractRefreshDialog/RefreshSelfServiceScheduleDialog';
import { ITypeaheadSource } from '~src/httpClient/fetchers/ITypeaheadSource';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { WebHttpClientWithErrorToastInstance } from '~src/httpClient/WebHttpClientWithErrorToast';
import { openCreateExtractTaskActionDialog, PropsForCreateExtractTaskActionBody } from '~src/schedule/components/CreateExtractTaskActionBody';
import { CreateExtractTasksSelfServiceSchedulesRequest, RefreshTargetType, RefreshTaskType } from '~src/schedule/serverApi/requests/CreateSelfServiceScheduleExtractTasksRequest';
import { SelfServiceSchedule } from '~src/schedule/serverApi/types/SelfServiceSchedule';
import { ISchedule } from '~src/schedule/types/ISchedule';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { IExtractItemInfo } from '~src/spacesPlatform/models/resourceInfoParts';
import { selectContentTotalLength } from '~src/spacesPlatform/selectors/ContentSelectors';
import { hasRunNowPermission } from '~src/spacesPlatform/utils/MixedContentHelpers';
import { IWorkbookBase } from '~src/workbook/models/IWorkbookInfo';

import { CreateBridgeExtractTaskActionService } from './bridge/CreateBridgeExtractTaskAction';

export enum ExtractActionType {
  createExtracts = 'CreateExtracts',
  encryptExtract = 'EncryptExtract',
  decryptExtract = 'DecryptExtract'
}

export enum CreateExtractTasksMode {
  AllowRefreshNow,
  AllowScheduleOnServer,
  AllowScheduleOnServerAndClient,
  AllowRefreshNowAndOnServer,
  AllowRefreshNowAndOnServerAndClient,
  AllowScheduleOnlyOnClient,
  DisableScheduleOnServerAndClient
}

export interface IExtractTaskTypeOption {
  labelTranslateKey: string;
  value: string;
  tbTestId: string;
}

export interface IRemoteRefreshTypeOption {
  refreshType: string;
}

export interface IExtractTaskSchedule {
  schedule?: ISchedule;
  type?: string;
}

export interface ICreateExtractTasks {
  dialog: {
    mode: string;   // See DialogMode
    showModeSelector: boolean;
    showRefreshNowTab: boolean;
    showRefreshOnClientTab: boolean;
    showScheduleRefreshTab: boolean;
  };

  extractTask: IExtractTaskSchedule;

  scheduleOnServerPrompt: string;
  scheduleOnClientPrompt: string;
  scheduleSource: ITypeaheadSource;

  refreshNowPrompt: string;

  allowIncremental: boolean;
  extractTaskTypeOptions?: IExtractTaskTypeOption[];

  itemTextFn?: (schedule: ISchedule) => string;
  displayScheduleDescriptionAsName: boolean;

  // The followings are only for shedules to be run on Bridge Clients
  selfServiceSchedulePaneProps: SelfServiceSchedulePaneProps;
  remoteRefreshPropertiesEditModel: IRemoteRefreshPropertiesEditModel;
  existingRemoteRefreshPropertiesEditModel: IRemoteRefreshPropertiesEditModel;
  remoteAgentOptions: IRemoteRefreshAgentOption[];
  remoteAvailableTypeOptions: string[];
  remoteRefreshTypeOption: IRemoteRefreshTypeOption;

  existingRefreshSchedulesCount: Number;
}

export type ExtractType = IWorkbookBase | IDataSourceBase;

export namespace CreateExtractTaskActionService {
  export function dialogTitle(allowedMode: CreateExtractTasksMode) {
    switch (allowedMode) {
      case CreateExtractTasksMode.AllowRefreshNowAndOnServer:
      case CreateExtractTasksMode.AllowRefreshNowAndOnServerAndClient:
      case CreateExtractTasksMode.DisableScheduleOnServerAndClient:
        return Messages.extractTasksDialogTitle_AllowBoth();
      case CreateExtractTasksMode.AllowScheduleOnlyOnClient:
        return Messages.extractTasksDialogTitle_ScheduleLegacyBridgeRefresh();
      case CreateExtractTasksMode.AllowRefreshNow:
        return Messages.extractTasksDialogTitle_AllowRefreshNow();
      case CreateExtractTasksMode.AllowScheduleOnServer:
      case CreateExtractTasksMode.AllowScheduleOnServerAndClient:
        return Messages.extractTasksDialogTitle_AllowSchedule();
      default:
        throw 'Unknown allowed mode: ' + allowedMode;
    }
  }

  export function extractErrorMessageFor(errorItem: IErrorItem<IExtractItemInfo>, schedule: ISchedule) {
    switch (errorItem.error.code) {
      case SchedulingErrorCode.SchedulingScheduleNotFound:
        return Messages.scheduleNotFound({ SCHEDULE: schedule.name });
      case WebClientErrorCode.NotSupportedForRemotelyRefreshedContent:
        return Messages.refreshNowRemotelyNotSupported();
    }
    return Messages.errorUnknown();
  }

  export function getRefreshSSSDialogParams(targetResources: (IDataSourceBase | IWorkbookBase)[]): OpenRefreshSelfServiceScheduleDialogParameters {
    // Bridge Related
    const existingRefreshSchedulesCount = selectContentTotalLength(getAppStore().getState());
    const datasources = targetResources.filter(c => c.contentType === 'datasource');
    const showSchedulesWillBeDeletedWarning = existingRefreshSchedulesCount > 0 && RefreshPropertyHelper.allDatasourcesHaveLegacyBridgeSchedules(datasources as IDataSourceDetailInfo[]);

    // Target Type
    const capitalizeString = (s: string) => s[0].toUpperCase() + s.substring(1);
    const targetTypes = targetResources.map(content => content.contentType).map(capitalizeString);
    const allSameTargetType = _.unique(targetTypes).length === 1;
    const targetType = allSameTargetType
      ? targetTypes[0] as RefreshTargetType
      : 'Mixed';

    // Task Type
    const canSupportIncremental = targetResources.map(target => target.hasIncrementalExtract);
    const allowIncremental = _.all(canSupportIncremental);

    const quantity = targetResources.length;
    const name = quantity === 1
      ? targetResources[0].name
      : undefined;
    const save = (recurrence: Recurrence, taskType: RefreshTaskType, scheduleName: string): Promise<IResult> => {
      const request = new CreateExtractTasksSelfServiceSchedulesRequest({
        schedule: SelfServiceSchedule.fromRecurrence(recurrence, 'Extract'),
        scheduleName: scheduleName.substring(0, 255),
        targetIds: targetResources.map(content => Number(content.id)),
        targetType,
        type: taskType,
      });
      return new Promise<IResult>((resolve, reject) => {
        WebHttpClientWithErrorToastInstance.sendRequest(request).then(result => {
          CreateExtractRefreshAction.notifyResult(result, true);
          resolve(result);
        }).catch(err => {
          CreateExtractRefreshAction.notifyResult();
          reject(err);
        });
      });
    };
    return {
      quantity, targetType, name,
      save: save,
      isNew: true,
      taskType: allowIncremental ? 'IncrementExtract' : 'RefreshExtract',
      disableSelectingTaskType: !allowIncremental,
      showExistingTasksWillBeDeletedWarning: showSchedulesWillBeDeletedWarning,
    };
  }

  export async function execute<T extends IExtractItemInfo & IContentInfo>(
    items: Set<T>,
    scheduleOnServerPrompt: string,
    scheduleOnClientPrompt: string,
    scheduleRefreshActionOnServer: (ids: string[], scheduleId: string, type: string) => Promise<IResult>,
    scheduleRefreshActionOnClientReact: (refreshType: string, recurrence: Recurrence, remoteAgentName: string) => Promise<IResult>,
    refreshNowPrompt: string,
    refreshNowAction: (ids: string[], type: string) => Promise<IResult>,
    errorDetailFn: (schedule: ISchedule) => (errorItem: IErrorItem<T>) => IErrorDetail,
    allowedMode: CreateExtractTasksMode
  ): Promise<IResult> {
    // The legacy logic uses the schedule for some rare error messages, only for the recommended tab
    // As such we do something similar by exposing the schedule to the error function if needed
    let scheduleForError = undefined as ISchedule;
    const scheduleFn = (type: RefreshTaskType, schedule: ISchedule) => {
      const itemIds = items.keys();
      scheduleForError = schedule;
      return scheduleRefreshActionOnServer(itemIds, schedule.id, type);
    };
    const runNowFn = (type: RefreshTaskType) => {
      const itemIds = items.keys();
      return refreshNowAction(itemIds, type);
    };
    const useDescriptionAsName = SessionServerSelectors.displayScheduleDescriptionAsName(getAppStore().getState());

    // Bridge has a lot of bridge specific logic
    const allowBridge = (allowedMode === CreateExtractTasksMode.AllowRefreshNowAndOnServerAndClient ||
      allowedMode === CreateExtractTasksMode.AllowScheduleOnServerAndClient ||
      allowedMode === CreateExtractTasksMode.AllowScheduleOnlyOnClient)
      && items.size() === 1;
    const firstItem = items.first();
    const remoteAgentTimeZone = allowBridge && 'remoteAgentTimeZone' in firstItem
      ? (firstItem as undefined as IDataSourceDetailInfo).remoteAgentTimeZone
      : null;
    // Method actually accepts null TZ name, contrary to what it declares
    const sssPaneProps = CreateBridgeExtractTaskActionService.getDefaultSelfServiceSchedulePanePropsForSSSWidget(remoteAgentTimeZone);
    const existingRefreshSchedulesCount = selectContentTotalLength(getAppStore().getState());
    const datasources: IDataSourceDetailInfo[] = allowBridge
      ? items.toArray().filter(c => c.contentType === 'datasource').map(d => d as undefined as IDataSourceDetailInfo)
      : [];
    const showExistingTasksWillBeDeleteWarning = allowBridge && existingRefreshSchedulesCount > 0 && RefreshPropertyHelper.allDatasourcesHaveOnlineBridgeSchedules(datasources);

    const remoteAgentOptions = allowBridge
      ? await CreateBridgeExtractTaskActionService.getRemoteAgentOptions(firstItem as undefined as IDataSourceDetailInfo)
      : [];
    const scheduleBridgeExtract = (recurrence: Recurrence, remoteAgentName: string, refreshType: string) => {
      return scheduleRefreshActionOnClientReact(refreshType, recurrence, remoteAgentName);
    };

    // Technically this means the user can think they're scheduling everything as incremental when that would be impossible
    // but that's current behavior so it stays
    const allowIncremental = _.any(items.toArray(), (item: IExtractItemInfo) => item.hasIncrementalExtract);
    const targetTypeAllowsRunNow = allowedMode === CreateExtractTasksMode.AllowRefreshNowAndOnServer
      || allowedMode === CreateExtractTasksMode.AllowRefreshNowAndOnServerAndClient ||
      allowedMode === CreateExtractTasksMode.DisableScheduleOnServerAndClient;
    const runNowEnabled = hasRunNowPermission(getAppStore().getState());
    const allowRunNow = targetTypeAllowsRunNow && runNowEnabled;
    const allowRecommended = allowedMode === CreateExtractTasksMode.AllowScheduleOnServer
      || allowedMode === CreateExtractTasksMode.AllowScheduleOnServerAndClient
      || allowedMode === CreateExtractTasksMode.AllowRefreshNowAndOnServer
      || allowedMode === CreateExtractTasksMode.AllowRefreshNowAndOnServerAndClient;
    const dialogProps: Omit<PropsForCreateExtractTaskActionBody, 'close'> = {
      renderRunNowTab: allowRunNow,
      renderRecommendedTab: allowRecommended,
      renderBridgeTab: allowBridge,
      schedule: scheduleFn,
      useDescriptionAsName,
      runNow: runNowFn,
      disableIncremental: !allowIncremental,
      runNowTabPrompt: refreshNowPrompt,
      description: scheduleOnServerPrompt,
      remoteAgentOptions,
      showExistingTasksWillBeDeleteWarning,
      scheduleBridgeExtract,
      recurrence: sssPaneProps.recurrenceModel,
      scheduleOnClientPrompt,
    };
    const dialog = openCreateExtractTaskActionDialog(dialogProps);
    const result = dialog.result.then(result => {
      if (result) {
        return ActionHelpers.displayStandardToastsForResult(
          items,
          result,
          {
            success: standardActionSuccessMsg(Messages.extractTasksCreateSuccess),
            error: standardActionErrorMsg(Messages.extractTasksCreateError),
          },
          (errorItem: IErrorItem<T>) => errorDetailFn(scheduleForError)(errorItem)
        );
      }
    });
    return result;
  }
}
